import submitContactForm from "@iewccouk/Store/Handlers/ContactUsForm/SubmitContactForm";
import { validateFormWithScroll } from "@iewccouk/Utilities/formValidationScroll";
import { colorStrings } from "@iewccouk/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
    leadSource = "leadSource",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
        [fields.leadSource]: string;
    };
}

const mapDispatchToProps = {
    submitContactForm,
    clearForm,
    /* Verndale Custom Feature - Load countries for contact us form page */
    loadCurrentCountries,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;

    /* Verndale Custom Feature - New styles */
    reCaptchaWrapper?: InjectableCss;
    messageErrorTextGridItem?: GridItemProps;
    messageErrorText?: TypographyPresentationProps;
}

export const contactFormStyles: ContactFormStyles = {
    formGridItem: { width: 12 },
    submitButton: {
        css: css`
            float: none;
            margin-top: 20px;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    messageErrorTextGridItem: {
        width: 12,
        css: css`
            color: ${getColor(colorStrings.primaryMain)};
        `,
    },
    messageErrorText: {
        css: css`
            line-height: 25px !important;
        `,
    },
};

const styles = contactFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const ContactForm = ({
    id,
    fields,
    submitContactForm,
    clearForm,
    validateReCaptcha: widgetValidateReCaptcha,

    /* Verndale Custom Feature - New method */
    loadCurrentCountries,
}: Props) => {
    const { emailRecipients, submitButtonText, successMessage, leadSource } = fields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isModalErrorOpen, setIsModalErrorOpen] = useState(false);

    /* Verndale Custom Feature - Load countries for contact us form page */
    useEffect(() => {
        loadCurrentCountries();
    }, []);

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Validate form fields with automatic scrolling to first error
        const { isValid, failedFields } = validateFormWithScroll(validators, "contactForm", {
            offset: 120, // Adjust offset based on your fixed header height
            behavior: "smooth",
        });

        // Always validate ReCaptcha to show error message, regardless of form field validation
        const isReCaptchaValid = await widgetValidateReCaptcha({
            location: "ContactUs",
            recaptchaRef: recaptchaRef as RefObject<ReCAPTCHA>,
        });

        if (!isValid) {
            return;
        }

        if (!isReCaptchaValid) {
            // Scroll to ReCaptcha when validation fails
            const recaptchaElement = recaptchaRef.current?.props.id
                ? document.getElementById(recaptchaRef.current.props.id)
                : document.querySelector("[data-sitekey]")?.closest('[class*="wrapper"]');

            if (recaptchaElement) {
                const elementTop = recaptchaElement.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementTop - 120; // Same offset as form validation

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                });
            }
            return;
        }

        setIsSubmitting(true);
        submitContactForm({
            emailRecipients,
            leadSource,
            onSuccess: data => {
                if (data.successful) {
                    setIsModalOpen(true);
                    setIsSubmitting(false);
                    clearForm();
                    // set session storage key "clearUploadFiles" to true to clear the uploaded files
                    sessionStorage.setItem("clearUploadFiles", "true");
                } else {
                    setErrorMessage(data.result.Message);
                    setIsSubmitting(false);
                    setIsModalErrorOpen(true);
                }
            },
        });
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
        setIsModalErrorOpen(false);
    };

    const modalErrorCloseHandler = () => {
        setIsModalErrorOpen(false);
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <ContactFormContext.Provider value={{ validators }}>
                    <form id="contactForm" onSubmit={handleFormSubmit} noValidate className="contact-form__wrapper">
                        <Zone contentId={id} zoneName="Content" />

                        <StyledWrapper {...styles.reCaptchaWrapper}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                        </StyledWrapper>

                        <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                            {submitButtonText}
                        </Button>

                        <Modal
                            headline={translate("Message Sent")}
                            contentLabel={successMessage}
                            {...styles.successModal}
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                        >
                            <GridContainer {...styles.modalContainer}>
                                <GridItem {...styles.messageTextGridItem}>
                                    <Typography {...styles.messageText}>{successMessage}</Typography>
                                </GridItem>
                                <GridItem {...styles.buttonGridItem}>
                                    <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>

                        <Modal
                            headline={translate("Error Message")}
                            contentLabel={errorMessage}
                            {...styles.successModal}
                            isOpen={isModalErrorOpen}
                            handleClose={modalErrorCloseHandler}
                        >
                            <GridContainer {...styles.modalContainer}>
                                <GridItem {...styles.messageErrorTextGridItem}>
                                    <Typography as="p" {...styles.messageErrorText}>
                                        {errorMessage.split("\n").map((line, index) => (
                                            <span key={index}>
                                                {line}
                                                <br />
                                            </span>
                                        ))}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.buttonGridItem}>
                                    <Button {...styles.continueButton} onClick={continueButtonClickHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                    </form>
                </ContactFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ContactForm as any) as any,
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: emailsRegexp,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
            {
                name: fields.leadSource,
                displayName: "Lead Source Type",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
